import type { IOrderLine, TOrderDealSnapshot } from '@team-milva/lab-models/resources'
import type { TPricingSegmentDealEntry } from '@team-milva/lab-models/pricingSegment'
import type { TCustomerAccountPriceDealEntry } from '@team-milva/lab-models/customerAccount'

/**
 * Seed-side port of `resolveEffectiveDeal`/`buildAnalysisOrderLines` from
 * `lab-cloud`'s `buildDealAwareOrderLines.ts` — that file lives in a different
 * repo and isn't importable here, so the same deal resolution/line-building
 * math is duplicated deliberately (not a shared utility) so seeded orders for
 * a dealt analysis materialize real tiered/interval lines and an `orderDeals`
 * snapshot, exercising the same deal/invoice flows a real order would. Keep
 * this in sync with the lab-cloud original if the deal math there changes.
 */
export function resolveEffectiveDeal(
  analysisID: string,
  payerSegmentDeals: TPricingSegmentDealEntry[],
  payerOwnDeals: TCustomerAccountPriceDealEntry[]
): TPricingSegmentDealEntry | null {
  const own = payerOwnDeals.find((deal) => deal.analysisID === analysisID)
  if (own) {
    if (own.entryType === 'disabled') {return null}
    return own.dealType === 'tiered-pricing'
      ? {
          analysisID: own.analysisID,
          dealType: 'tiered-pricing',
          tier1: own.tier1,
          tier2: own.tier2,
        }
      : {
          analysisID: own.analysisID,
          dealType: 'interval-pricing',
          intervalCount: own.intervalCount,
          intervalPrice: own.intervalPrice,
        }
  }
  return (
    payerSegmentDeals.find((deal) => deal.analysisID === analysisID) ?? null
  )
}

/** Splits a total quantity across ascending tier thresholds — one count per bracket. */
function splitQuantityAcrossTierThresholds(
  totalQuantity: number,
  thresholds: number[]
): number[] {
  const starts = [1, ...thresholds]
  return starts.map((start, index) => {
    const nextStart = starts[index + 1]
    const end = nextStart !== undefined ? nextStart - 1 : Infinity
    if (totalQuantity < start) {return 0}
    return Math.min(totalQuantity, end) - start + 1
  })
}

function buildTieredLines({
  analysisID,
  quantity,
  tier1,
  tier2,
  basePriceValue,
}: {
  analysisID: string
  quantity: number
  tier1: { quantity: number; priceValue: number }
  tier2: { quantity: number; priceValue: number } | null
  basePriceValue: number | null
}): IOrderLine[] {
  const thresholds = [tier1.quantity, ...(tier2 ? [tier2.quantity] : [])]
  const [baseQty, tier1Qty, tier2Qty] = splitQuantityAcrossTierThresholds(quantity, thresholds)
  const hasBasePrice = basePriceValue !== null
  const lines: IOrderLine[] = []
  if (baseQty && baseQty > 0) {
    const unitPrice = hasBasePrice ? basePriceValue : 0
    lines.push({
      type: 'analysis',
      analysisID,
      quantity: baseQty,
      unitPrice,
      total: baseQty * unitPrice,
      options: { isPriceUnknown: !hasBasePrice },
    })
  }
  if (tier1Qty && tier1Qty > 0) {
    lines.push({
      type: 'analysis',
      analysisID,
      dealTierIndex: 1,
      quantity: tier1Qty,
      unitPrice: tier1.priceValue,
      total: tier1Qty * tier1.priceValue,
      options: { isPriceUnknown: false },
    })
  }
  if (tier2 && tier2Qty && tier2Qty > 0) {
    lines.push({
      type: 'analysis',
      analysisID,
      dealTierIndex: 2,
      quantity: tier2Qty,
      unitPrice: tier2.priceValue,
      total: tier2Qty * tier2.priceValue,
      options: { isPriceUnknown: false },
    })
  }
  if (lines.length === 0) {
    const unitPrice = hasBasePrice ? basePriceValue : 0
    lines.push({
      type: 'analysis',
      analysisID,
      quantity: 0,
      unitPrice,
      total: 0,
      options: { isPriceUnknown: !hasBasePrice },
    })
  }
  return lines
}

export function buildAnalysisOrderLines({
  analysisID,
  quantity,
  basePriceValue,
  deal,
}: {
  analysisID: string
  quantity: number
  basePriceValue: number | null
  deal: TPricingSegmentDealEntry | null
}): { lines: IOrderLine[]; dealSnapshot: TOrderDealSnapshot | null } {
  const hasBasePrice = basePriceValue !== null

  if (!deal) {
    const unitPrice = hasBasePrice ? basePriceValue : 0
    return {
      lines: [
        {
          type: 'analysis',
          analysisID,
          quantity,
          unitPrice,
          total: quantity * unitPrice,
          options: { isPriceUnknown: !hasBasePrice },
        },
      ],
      dealSnapshot: null,
    }
  }

  if (deal.dealType === 'tiered-pricing') {
    return {
      lines: buildTieredLines({
        analysisID,
        quantity,
        tier1: deal.tier1,
        tier2: deal.tier2,
        basePriceValue,
      }),
      dealSnapshot: {
        analysisID,
        dealType: 'tiered-pricing',
        tier1: { tierIndex: 1, quantity: deal.tier1.quantity, priceValue: deal.tier1.priceValue },
        tier2: deal.tier2
          ? { tierIndex: 2, quantity: deal.tier2.quantity, priceValue: deal.tier2.priceValue }
          : null,
        basePriceValue: hasBasePrice ? basePriceValue : null,
      },
    }
  }

  // interval-pricing
  const intervals = Math.ceil(quantity / deal.intervalCount)
  const total = intervals * deal.intervalPrice
  const unitPrice = quantity > 0 ? Math.round((total / quantity) * 100) / 100 : 0
  return {
    lines: [
      {
        type: 'analysis',
        analysisID,
        quantity,
        unitPrice,
        total,
        options: { isPriceUnknown: false },
      },
    ],
    dealSnapshot: {
      analysisID,
      dealType: 'interval-pricing',
      intervalCount: deal.intervalCount,
      intervalPrice: deal.intervalPrice,
      orderedQuantity: quantity,
    },
  }
}
